/**
 * Diversion on the canonical ticket.
 * destIcao stays the original intended dest. divertToIcao + divertReason +
 * lifecycle diverting|diverted are the live fields Megan / auto-land write.
 */
import { safeTrim } from "./text";
import { isWxIcao, normalizeIcao } from "./weather";
import type { Mission, TicketLifecycle } from "./types";

export type DiversionPhase = "diverting" | "diverted";

export const DIVERT_REASON_MAX = 80;

export function parseDivertIcao(raw: unknown): string {
  const icao = normalizeIcao(raw);
  return isWxIcao(icao) ? icao : "";
}

export function shortDivertReason(raw: unknown): string {
  const text = safeTrim(raw).replace(/\s+/g, " ");
  if (!text) return "";
  return text.length > DIVERT_REASON_MAX ? `${text.slice(0, DIVERT_REASON_MAX - 1)}…` : text;
}

export function isDiversionLifecycle(life?: TicketLifecycle | null): boolean {
  return life === "diverting" || life === "diverted";
}

export function formatDiversionLine(
  row: Pick<Mission, "divertToIcao" | "divertReason"> & { lifecycle?: string | null; status?: string | null },
): string {
  const to = parseDivertIcao(row.divertToIcao);
  const reason = shortDivertReason(row.divertReason);
  if (!to && !reason) return "";
  const landed = row.lifecycle === "diverted" || row.status === "diverted";
  const verb = landed ? "Diverted to" : "Diverting to";
  const dest = to || "—";
  return reason ? `${verb} ${dest} · ${reason}` : `${verb} ${dest}`;
}

export type DiversionInput = {
  toIcao: string;
  reason?: string;
  phase?: DiversionPhase;
  at?: string;
  /** Auto land≠filed dest may omit a spoken reason. Megan still needs one. */
  requireReason?: boolean;
};

export type DiversionResult = { ok: true; mission: Mission } | { ok: false; error: string };

export function applyDiversion(mission: Mission, input: DiversionInput): DiversionResult {
  if (mission.status === "cancelled" || mission.lifecycle === "cancelled") {
    return { ok: false, error: `${mission.callsign || "Trip"} is cancelled — will not divert.` };
  }
  if (mission.status === "completed" || mission.lifecycle === "completed") {
    return { ok: false, error: `${mission.callsign || "Trip"} is completed — will not divert.` };
  }
  const to = parseDivertIcao(input.toIcao);
  if (!to) return { ok: false, error: "Need a real divert ICAO." };
  const reason = shortDivertReason(input.reason);
  if (input.requireReason !== false && !reason) {
    return { ok: false, error: "Need a short divert reason (what the pilot said)." };
  }
  const filed = normalizeIcao(mission.destIcao);
  if (filed && to === filed) {
    return { ok: false, error: `${to} is the filed dest — not a diversion.` };
  }
  const phase: DiversionPhase = input.phase === "diverted" ? "diverted" : "diverting";
  const at = input.at ?? new Date().toISOString();
  return {
    ok: true,
    mission: {
      ...mission,
      lifecycle: phase,
      status: "in-progress",
      divertToIcao: to,
      divertReason: reason || mission.divertReason,
      startedAt: mission.startedAt ?? at,
      arrivedAtUtc: phase === "diverted" ? (mission.arrivedAtUtc ?? at) : mission.arrivedAtUtc,
    },
  };
}
